import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CONTACT_SECTION_KEYS,
  fetchContactPage,
  findSection,
} from "../services/contentApi";

const ContactPageContext = createContext(null);

export function ContactPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchContactPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching contact page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    CONTACT_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    if (!result["contact-banner"] && page?.sections) {
      result["contact-banner"] =
        page.sections.find(
          (s) =>
            (s.sectionType || s.type || "").toLowerCase() === "contact-banner"
        ) ?? null;
    }
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <ContactPageContext.Provider value={value}>
      {children}
    </ContactPageContext.Provider>
  );
}

export function useContactPage() {
  const ctx = useContext(ContactPageContext);
  if (!ctx) {
    throw new Error("useContactPage must be used within ContactPageProvider");
  }
  return ctx;
}

export function useContactSection(key) {
  const ctx = useContext(ContactPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromContactPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromContactPage: true,
  };
}
